import React, { useState, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../../components/ui/typography/Text';
import { Input } from '../../../components/ui/Input';
import { AccentButton } from '../../../components/ui/buttons/AccentButton';
import { Brain, Microscope, BookOpen, LineChart } from 'lucide-react';
import { LogicalTest, questions } from './LogicalTest';
import { ResultsGraph } from './ResultsGraph';

/**
 * Dunning-Kruger Effect Simulation
 * 
 * This simulation demonstrates how people with low ability tend to overestimate
 * their skills, while high performers tend to underestimate themselves.
 * Based on the 1999 study by Dunning & Kruger.
 */
type SimulationStage = 'intro' | 'self-assessment' | 'test' | 'results';

const TemplatedDunningKrugerSimulation: React.FC = () => {
  // ========== STATE MANAGEMENT ==========
  const [started, setStarted] = useState(false);
  const [stage, setStage] = useState<SimulationStage>('intro');
  const [initialConfidence, setInitialConfidence] = useState<string>('');
  const [answers, setAnswers] = useState<number[]>([]);
  const [score, setScore] = useState<number>(0);
  const [error, setError] = useState('');
  
  const simulationRef = useRef<HTMLDivElement>(null);

  // ========== EVENT HANDLERS ==========
  const handleConfidenceChange = (value: string) => {
    setError('');
    const numValue = value.replace(/^0+|[^\d]/g, '');
    if (numValue === '' || (parseInt(numValue, 10) <= 100)) {
      setInitialConfidence(numValue);
    }
  };

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  const calculateScore = () => {
    let correct = 0;
    answers.forEach((answer, index) => {
      if (answer === questions[index].correctOption) {
        correct++;
      }
    });
    return (correct / questions.length) * 100;
  };

  const handleSubmitTest = () => {
    if (answers.length !== questions.length) {
      setError('Please answer all questions before proceeding');
      return;
    }
    const finalScore = calculateScore();
    setScore(finalScore);
    setStage('results');
  };

  const handleSubmitConfidence = () => {
    if (initialConfidence === '') {
      setError('Please enter your confidence level (0-100)');
      return;
    }
    
    const value = parseInt(initialConfidence, 10);
    if (isNaN(value) || value < 0 || value > 100) {
      setError('Please enter a valid number between 0 and 100');
      return;
    }

    setError('');
    setStage('test');
  };

  const handleReset = () => {
    setStarted(false);
    setStage('intro');
    setInitialConfidence('');
    setAnswers([]);
    setScore(0);
    setError('');
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ========== SIMULATION CONTENT ==========
  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* Initial Self-Assessment */}
      <SimulationStep 
        title="Initial Self-Assessment" 
        isActive={stage === 'self-assessment'}
      >
        <div className="space-y-4">
          <Text className="text-gray-700">
            Before taking the test, how do you think you'll perform compared to others?
            Please estimate which percentile you think you'll score in (0-100).
          </Text>
          <div className="max-w-xs mx-auto">
            <Input
              type="number"
              value={initialConfidence}
              onChange={(e) => handleConfidenceChange(e.target.value)}
              disabled={stage !== 'self-assessment'}
              placeholder="Enter your expected percentile (0-100)"
              min={0}
              max={100}
            />
            {initialConfidence && (
              <Text className="text-center mt-2">Your expected percentile: {initialConfidence}%</Text>
            )}
          </div>
          {error && <Text className="text-red-500 text-center">{error}</Text>}
          {stage === 'self-assessment' && (
            <div className="flex justify-center mt-4">
              <AccentButton onClick={handleSubmitConfidence}>
                Continue
              </AccentButton>
            </div>
          )}
        </div>
      </SimulationStep>

      {/* Logical Reasoning Test */}
      {stage !== 'self-assessment' && (
        <SimulationStep 
          title="Logical Reasoning Test"
          isActive={stage === 'test'}
        >
          <Text className="mb-6 text-gray-700">
            Please answer the following logical reasoning questions:
          </Text>
          <LogicalTest
            answers={answers}
            onAnswerSelect={handleAnswerSelect}
          />
          {error && <Text className="text-red-500 mt-4">{error}</Text>}
          {stage === 'test' && (
            <div className="flex justify-center mt-6">
              <AccentButton onClick={handleSubmitTest}>
                See Results
              </AccentButton>
            </div>
          )}
        </SimulationStep>
      )}

      {/* Results */}
      {stage === 'results' && (
        <div className="space-y-6">
          {/* Understanding Results Block */}
          <ResultBlock
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-8">
              {/* Step 1: User Results */}
              <div className="space-y-5">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>
                
                <div className="space-y-4">
                  <Text className="text-gray-700">Initial Self-Assessment: <span className="font-medium text-orange-600">{initialConfidence}th percentile</span></Text>
                  <Text className="text-gray-700">Actual Score: <span className="font-medium text-blue-600">{score.toFixed(1)}%</span></Text>
                </div>
                
                <ResultMessage 
                  isCorrect={Math.abs(parseInt(initialConfidence) - score) < 15} 
                  message={Math.abs(parseInt(initialConfidence) - score) < 15 
                    ? "Your self-assessment was quite accurate compared to your actual performance!"
                    : parseInt(initialConfidence) > score
                      ? "You overestimated your performance, which is a common finding in the Dunning-Kruger effect."
                      : "You underestimated your performance, which is common among higher performers."}
                />
                
                <GraphContainer>
                  <ResultsGraph
                    userInitialConfidence={initialConfidence}
                    userScore={score}
                  />
                </GraphContainer>
              </div>
              
              {/* Step 2: Connection to the Bias */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to Dunning-Kruger Effect
                </Text>
                <Text className="text-gray-700">
                  This experiment demonstrates how our perception of our own abilities can be skewed. 
                  The Dunning-Kruger effect describes how people with low ability tend to overestimate their skills, 
                  while high performers often underestimate their relative standing.
                </Text>
              </div>

              {/* Step 3: Study Findings */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <Text className="text-gray-700">
                  In their 1999 study at Cornell University, Dunning and Kruger tested students in three areas:
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Logical reasoning (similar to this simulation)</li>
                  <li>Grammar</li>
                  <li>Humor</li>
                </ul>
                <Text className="text-gray-700 mt-4">
                  Their key findings revealed:
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Participants in the bottom quartile scored at the 12th percentile but estimated themselves at the 68th percentile</li>
                  <li>Top performers were more accurate but slightly underestimated their performance</li>
                  <li>Poor performers were less able to recognize competence in others</li>
                  <li>Improving participants' skills helped them recognize their previous errors</li>
                </ul>
              </div>
            </div>
          </ResultBlock>

          {/* About the Study Block */}
          <ResultBlock
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by Dunning & Kruger (1999), "Unskilled and Unaware of It: How Difficulties in Recognizing One's Own Incompetence Lead to Inflated Self-Assessments," published in the Journal of Personality and Social Psychology. The study demonstrated how cognitive biases can prevent people from accurately evaluating their own abilities.
              </Text>
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          {/* Try Again Button */}
          <div className="flex justify-center">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <SimulationTemplate
      title="The Dunning-Kruger Effect"
      description="Experience how our self-assessment of competence relates to actual performance in this recreation of the famous 1999 study by David Dunning and Justin Kruger."
    >
      {!started ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => {
            setStarted(true);
            setStage('self-assessment');
          }}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default TemplatedDunningKrugerSimulation; 